import { useMemo } from "react";

import type { AccessRole, AssignedRole, EffectiveSummary } from "../types/access.types";
import { gatherStatements, summarize } from "../utils/denyFirst";

/**
 * Per-principal effective-access summaries, computed **once per (assignments, catalog) change** instead of
 * inline inside every row's render. Both the Usuarios and API Keys sections rendered
 * `summarize(gatherStatements(...))` inside `filtered.map`, so each keystroke in the search box (local state)
 * re-ran the whole deny-first resolution for every visible principal. Keying this memo on the assignment map
 * + the roles catalog (and NOT on the search query) means typing no longer recomputes anything; summaries
 * refresh only when roles or assignments actually change.
 *
 * Computed for every principal in `rolesByPrincipal`, not just the currently filtered rows — keying on the
 * filtered subset would reintroduce the per-keystroke recompute.
 */
export function useEffectiveSummaries(
  rolesByPrincipal: Record<string, AssignedRole[]>,
  roles: readonly AccessRole[],
): Record<string, EffectiveSummary> {
  return useMemo(() => {
    const out: Record<string, EffectiveSummary> = {};
    for (const [principalId, assigned] of Object.entries(rolesByPrincipal)) {
      out[principalId] = summarize(
        gatherStatements(
          assigned.map((role) => role.id),
          roles,
        ),
      );
    }
    return out;
  }, [rolesByPrincipal, roles]);
}
